/**
 * Formats seconds as `m:ss`, or `h:mm:ss` from one hour (or always, with `alwaysHours`).
 * Rounds to the nearest second.
 */
export function formatDuration(totalSec: number, options: { alwaysHours?: boolean } = {}): string {
  const rounded = Math.round(Math.abs(totalSec));
  const sign = totalSec < 0 && rounded > 0 ? "-" : "";
  const h = Math.floor(rounded / 3600);
  const m = Math.floor((rounded % 3600) / 60);
  const s = rounded % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0 || options.alwaysHours) {
    return `${sign}${h}:${String(m).padStart(2, "0")}:${ss}`;
  }
  return `${sign}${m}:${ss}`;
}

/**
 * Parses `ss`, `m:ss` or `h:mm:ss` (seconds may have a decimal part).
 * Returns null for anything else, including out-of-range minutes or seconds.
 */
export function parseDuration(input: string): number | null {
  const parts = input.trim().split(":");
  if (parts.length === 0 || parts.length > 3) return null;
  if (!parts.every((p, i) => (i === parts.length - 1 ? /^\d+(\.\d+)?$/ : /^\d+$/).test(p))) {
    return null;
  }
  const values = parts.map(Number);
  const [sec, min, hour] = [...values].reverse() as [number, number?, number?];
  if (parts.length > 1 && sec >= 60) return null;
  if (parts.length > 2 && min !== undefined && min >= 60) return null;
  return (hour ?? 0) * 3600 + (min ?? 0) * 60 + sec;
}

/** Formats a pace in seconds per km as `m:ss/km`. */
export function formatPace(secPerKm: number): string {
  return `${formatDuration(secPerKm)}/km`;
}

/** Formats a difference against plan as `+m:ss` (behind) or `-m:ss` (ahead). */
export function formatDelta(deltaSec: number): string {
  const formatted = formatDuration(deltaSec);
  return formatted.startsWith("-") ? formatted : `+${formatted}`;
}
